import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import api from "../services/api";
import { MIN_CREDIT_QUANTITY, MAX_CREDIT_QUANTITY } from "./creditQuantity";

const PRODUCT_FEATURES = [
  "1 website analysis per credit",
  "100 ranked SEO + GEO actions",
  "About 15-minute delivery",
  "30-day money-back",
];

function getHostHeader() {
  const currentHost = window.location?.hostname || "";
  let hostHeader = currentHost || "app.discoseo.com";

  if (
    currentHost === "localhost" ||
    currentHost.includes("discoseo") ||
    !currentHost
  ) {
    hostHeader = "app.discoseo.com";
  }

  return hostHeader;
}

function formatMoney(amount, currency = "usd") {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return null;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const PurchaseCreditsModal = ({ isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const [tier, setTier] = useState(null);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setQuantity(1);
      setError(null);
      setSubmitting(false);
      return;
    }

    let cancelled = false;

    const loadTiers = async () => {
      try {
        setLoadingTiers(true);
        setError(null);
        const response = await api.call("/subscription/tiers", {
          method: "GET",
          headers: { Host: getHostHeader() },
        });
        if (cancelled) return;
        const paidTiers = (response.data || []).filter(
          (item) => item.priceId && item.interval === "one_time",
        );
        setTier(paidTiers[0] || (response.data || [])[0] || null);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load pricing");
        }
      } finally {
        if (!cancelled) {
          setLoadingTiers(false);
        }
      }
    };

    loadTiers();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const unitPrice = tier?.price ?? null;
  const currency = tier?.currency || "usd";
  const total = unitPrice === null ? null : unitPrice * quantity;
  const totalLabel = formatMoney(total, currency);
  const unitLabel = formatMoney(unitPrice, currency);

  const quantityLabel = useMemo(
    () => `${quantity} credit${quantity === 1 ? "" : "s"}`,
    [quantity],
  );

  const clampQuantity = (value) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < MIN_CREDIT_QUANTITY) {
      return MIN_CREDIT_QUANTITY;
    }
    return Math.min(parsed, MAX_CREDIT_QUANTITY);
  };

  const handlePurchase = async () => {
    if (!tier?.key) {
      setError("Pricing is unavailable. Please try again.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await api.call("/subscription/create-checkout", {
        method: "POST",
        headers: { Host: getHostHeader() },
        body: {
          tier: tier.key,
          quantity,
          successUrl: `${window.location.origin}${window.location.pathname || "/"}${window.location.search || ""}`,
          cancelUrl: `${window.location.origin}${window.location.pathname || "/"}${window.location.search || ""}`,
        },
      });

      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
        return;
      }

      throw new Error(response.error || "Failed to start checkout");
    } catch (err) {
      setError(err.message || "Failed to start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Purchase credits" isOpen={isOpen} onClose={onClose} size="sm">
      <div className="px-6 py-5 space-y-6">
        {loadingTiers ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-zinc-300 border-t-zinc-900 dark:border-white/20 dark:border-t-white" />
            <span className="ml-3 text-sm text-zinc-600 dark:text-white/70">
              Loading pricing...
            </span>
          </div>
        ) : (
          <>
            <div className="text-center">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-white/55">
                {tier?.name || "SEO Report"}
              </h4>
              <p className="mt-2 text-4xl font-extrabold tracking-tight text-zinc-950 dark:text-white">
                {unitLabel || "—"}
              </p>
              <p className="mt-1 text-sm text-zinc-600 dark:text-white/70">
                per credit · one domain analysis
              </p>
            </div>

            <div>
              <label
                htmlFor="credit-quantity"
                className="block text-sm font-semibold text-zinc-900 dark:text-white mb-3 text-center"
              >
                How many credits?
              </label>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => setQuantity((current) => clampQuantity(current - 1))}
                  disabled={quantity <= MIN_CREDIT_QUANTITY || submitting}
                  className="h-12 w-12 rounded-lg bg-zinc-100 text-2xl font-semibold text-zinc-900 hover:bg-zinc-200 disabled:opacity-30 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                >
                  −
                </button>
                <input
                  id="credit-quantity"
                  type="number"
                  min={MIN_CREDIT_QUANTITY}
                  max={MAX_CREDIT_QUANTITY}
                  value={quantity}
                  onChange={(event) => setQuantity(clampQuantity(event.target.value))}
                  className="h-12 w-24 rounded-lg border border-zinc-300 bg-white px-3 text-center text-2xl font-bold text-zinc-950 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none dark:border-white/20 dark:bg-black/40 dark:text-white"
                />
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={() => setQuantity((current) => clampQuantity(current + 1))}
                  disabled={quantity >= MAX_CREDIT_QUANTITY || submitting}
                  className="h-12 w-12 rounded-lg bg-zinc-100 text-2xl font-semibold text-zinc-900 hover:bg-zinc-200 disabled:opacity-30 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg bg-zinc-100 px-4 py-3 dark:bg-white/10">
              <span className="text-sm text-zinc-600 dark:text-white/70">{quantityLabel}</span>
              <span className="text-xl font-bold text-zinc-950 dark:text-white">
                {totalLabel || "—"}
              </span>
            </div>

            <ul className="space-y-1.5 text-sm text-zinc-700 dark:text-white/80">
              {PRODUCT_FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <span className="mt-0.5 text-green-600 dark:text-green-400">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {error && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 dark:border-red-400/40 dark:bg-red-500/15">
                <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
              </div>
            )}

            <button
              type="button"
              onClick={handlePurchase}
              disabled={submitting || loadingTiers || !tier}
              className="w-full rounded-lg bg-zinc-900 py-3.5 px-4 text-base font-semibold text-white hover:bg-zinc-800 disabled:opacity-50 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
            >
              {submitting
                ? "Redirecting to checkout..."
                : `Purchase ${quantityLabel}${totalLabel ? ` — ${totalLabel}` : ""}`}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default PurchaseCreditsModal;
